import {
  ComplianceCaregiverRecord,
  ComplianceDocument,
  DashboardComplianceItem,
} from "../../models/compliance";
import { CaregiverRecord, CaregiverRecordList } from "../../scripts/messages/caregiver";
import { CaregiverDocumentTypeId, CaregiverId } from "../../scripts/messages/ids";
import { DatabaseApiService } from "../../scripts/services/db";
import { CompService } from "./comp.service";

export interface MissingItems {
  required: ComplianceDocument[];
  notRequired: ComplianceDocument[];
}

export interface WholeAgencyComplianceData {
  records: ComplianceCaregiverRecord[];
  documentTypes: ComplianceDocument[];
  caregiverMissingItemsMap: Map<CaregiverId, MissingItems>;
}

/**
 * The whole-agency compliance dataset the Items Status and Caregiver Status tabs
 * are built from.
 *
 * This is not an improvement on anything - it is every caregiver in the agency
 * and every compliance item in the agency, which is what the compliance page has
 * always loaded. It exists here so that the *Dashboard* tab no longer has to
 * produce it: with the caregiver memory improvements on, the dashboard is paged
 * and broadcasts nothing, and this is called lazily by `caregiversComplianceCtrl`
 * when one of the two whole-agency tabs is actually opened. Landing on the page
 * costs nothing.
 *
 * Paging those two tabs is the remaining piece of work. When that is done, this
 * goes, and with it the last full-agency caregiver read on this page.
 */
//! @ngInject
export class ComplianceWholeAgencyService {
  constructor(
    private compService: CompService,
    private DatabaseApi: DatabaseApiService,
    private $rootScope: ng.IRootScopeService
  ) {}

  load = (): ng.IPromise<WholeAgencyComplianceData> => {
    return this.fetchCaregivers().then((caregiversMap) =>
      this.compService.fetch(caregiversMap).then((data) =>
        this.compService.fetchMissingFollowupItems().then((missingFollowupItems) => {
          const documentTypes = data.documents;
          const missingFollowupMap = this.toMissingFollowupMap(missingFollowupItems);

          // A row for every caregiver, not only the ones the compliance endpoint
          // returned items for: a caregiver with no items still belongs in these
          // tables, as missing everything.
          const itemsByCaregiver = new Map(
            data.items.map((record) => [record.caregiver.id, record.items])
          );

          const caregiverMissingItemsMap: Map<CaregiverId, MissingItems> = new Map();
          const records: ComplianceCaregiverRecord[] = [];

          for (const caregiver of Object.values(caregiversMap)) {
            const items = itemsByCaregiver.get(caregiver.id) ?? [];

            caregiverMissingItemsMap.set(
              caregiver.id,
              this.getCaregiverMissingItems(caregiver, items, documentTypes, missingFollowupMap)
            );

            records.push({ caregiver, items });
          }

          return { records, documentTypes, caregiverMissingItemsMap };
        })
      )
    );
  };

  /**
   * Loads and then broadcasts on `got_compliance_data`, which is the signal the
   * two tabs listen on. Kept as the same event the old dashboard emitted so the
   * tabs did not have to change.
   */
  loadAndBroadcast = (): ng.IPromise<WholeAgencyComplianceData> => {
    return this.load().then((data) => {
      this.$rootScope.$emit("got_compliance_data", data);

      return data;
    });
  };

  private fetchCaregivers = (): ng.IPromise<Record<number, CaregiverRecord>> => {
    return this.DatabaseApi.get(
      `agencies/${this.$rootScope.agencyId}/agency_members/${this.$rootScope.agencyMemberId}/caregivers/records?withPhoneNumbers=true`
    ).then((res: { data: CaregiverRecordList }) => {
      const map: Record<number, CaregiverRecord> = {};

      for (const caregiver of res.data.caregivers) {
        map[caregiver.id as unknown as number] = caregiver;
      }

      return map;
    });
  };

  private toMissingFollowupMap = (
    items: { caregiverId: CaregiverId; documentTypeId: CaregiverDocumentTypeId }[]
  ): Map<CaregiverId, Set<CaregiverDocumentTypeId>> => {
    const map: Map<CaregiverId, Set<CaregiverDocumentTypeId>> = new Map();

    for (const { caregiverId, documentTypeId } of items) {
      const existing = map.get(caregiverId);

      if (existing === undefined) {
        map.set(caregiverId, new Set([documentTypeId]));
      } else {
        existing.add(documentTypeId);
      }
    }

    return map;
  };

  /**
   * The document types this caregiver ought to have and does not, split by
   * whether they are required. Lifted verbatim from the pre-paging dashboard,
   * which is the only place it used to live.
   */
  private getCaregiverMissingItems = (
    caregiver: CaregiverRecord,
    items: DashboardComplianceItem[],
    documentTypes: ComplianceDocument[],
    missingFollowupMap: Map<CaregiverId, Set<CaregiverDocumentTypeId>>
  ): MissingItems => {
    const present = new Set(items.map((item) => item.documentType.id));
    const missingItems: MissingItems = { required: [], notRequired: [] };

    for (const documentType of documentTypes) {
      if (present.has(documentType.id)) {
        continue;
      }

      if (documentType.followupParentId !== null && !present.has(documentType.followupParentId)) {
        continue;
      }

      if (!documentType.isRoot) {
        continue;
      }

      if (!documentType.certifications.some((cert) => caregiver.certifications.includes(cert))) {
        continue;
      }

      const isRequired =
        documentType.followupParentId === null ||
        (missingFollowupMap.get(caregiver.id)?.has(documentType.id) ?? false);

      missingItems[isRequired ? "required" : "notRequired"].push(documentType);
    }

    return missingItems;
  };
}
